/**
 * Strings for the candidate briefing ("Mi campaña hoy"). Every key starts
 * with `brief.`. Spread into both dictionaries in web/src/lib/i18n.ts; the
 * `satisfies` below makes a missing or extra Spanish key a type error here.
 */
export const briefingEn = {
  "brief.title": "My campaign today",
  "brief.greet.morning": "Good morning, {name}",
  "brief.greet.afternoon": "Good afternoon, {name}",
  "brief.greet.evening": "Good evening, {name}",
  "brief.daysToElection": "days to the election",
  "brief.oneDayToElection": "day to the election",
  "brief.electionDate": "Tuesday, November 3, 2026",
  "brief.electionToday": "Election day. Go get it.",
  "brief.electionOver": "The general election is over.",
  "brief.daysToRunoff": "days to the runoff",
  "brief.oneDayToRunoff": "day to the runoff",
  "brief.runoffDate": "Saturday, December 12, 2026",
  "brief.runoffToday": "Runoff day. Go get it.",
  "brief.milestone.today": "{title} is today",
  "brief.milestone.tomorrow": "{title} tomorrow",
  "brief.milestone.later": "{title} in {n} days",
  "brief.sec.next": "Next",
  "brief.next.callTime": "Start call time",
  "brief.next.practice": "Practice an answer",
  "brief.next.tasks": "{n} task(s) due today",
  "brief.next.tasksNone": "Nothing due today",
  "brief.next.scoreboard": "Add a supporter count",
  "brief.next.hoursToday": "{n} h this week",
  "brief.sec.scoreboard": "The scoreboard",
  "brief.sec.tasks": "On your plate today",
  "brief.sec.events": "Coming up",
  "brief.sec.media": "Media",
  "brief.sec.phrase": "Line of the day",
  "brief.supporters.label": "Identified supporters",
  "brief.supporters.of": "of {target}",
  "brief.supporters.pace": "{n} to go · about {w} a week",
  "brief.supporters.done": "Goal reached. Keep counting.",
  "brief.supporters.empty": "No count yet",
  "brief.supporters.emptyHint": "The field team records it on the scoreboard.",
  "brief.raised.label": "Raised",
  "brief.raised.of": "of {target} by {date}",
  "brief.raised.pace": "{amount} a day to get there",
  "brief.raised.reached": "The {date} target is met.",
  "brief.raised.allDone": "All report dates have passed. Final goal: {target}.",
  "brief.raised.empty": "No fundraising figure yet",
  "brief.raised.emptyHint": "The treasurer records it on the scoreboard.",
  "brief.raised.cash": "Cash on hand {amount}",
  "brief.asOf": "As of {date}",
  "brief.asOfStale": "As of {date} · out of date",
  "brief.asOfMissing": "No date",
  "brief.calls.label": "Call time this week",
  "brief.calls.ofHours": "of {target} h",
  "brief.calls.rate": "{amount}/hour pledged · goal {target}",
  "brief.calls.none": "No hours logged yet this week.",
  "brief.calls.open": "Open call time",
  "brief.alert.conduct.title": "Conduct alert",
  "brief.alert.conduct.body":
    "{n} unreviewed mention(s) about you. Talk to the campaign manager before any press.",
  "brief.alert.conduct.open": "Review in Watchdog",
  "brief.alert.moneyTrigger.title": "Money decision trigger",
  "brief.alert.moneyTrigger.body":
    "{amount} short of the agreed threshold. If we are still under on the trigger date, the pre-committed plan starts:",
  "brief.alert.moneyTrigger.plan": "the fallback budget the campaign agreed in advance.",
  "brief.alert.staleRaised.title": "The money figure is out of date",
  "brief.alert.staleRaised.body":
    "The figures are as of {date}. Ask the treasurer to update the scoreboard.",
  "brief.alert.staleSupporters.title": "The supporter count is out of date",
  "brief.alert.staleSupporters.body":
    "The count is as of {date}. Ask the field team to update the scoreboard.",
  "brief.tasks.empty": "Nothing pending. A good day to knock doors.",
  "brief.tasks.all": "See all",
  "brief.due.overdue": "{n} days late",
  "brief.due.yesterday": "Due yesterday",
  "brief.due.today": "Today",
  "brief.due.tomorrow": "Tomorrow",
  "brief.due.later": "In {n} days",
  "brief.due.none": "No date",
  "brief.priority.urgent": "Urgent",
  "brief.priority.high": "High",
  "brief.status.blocked": "Blocked",
  "brief.events.empty": "Nothing on the calendar yet.",
  "brief.events.all": "Calendar",
  "brief.media.opportunities": "Opportunities",
  "brief.media.opportunitiesEmpty": "No new opportunities.",
  "brief.media.openScout": "Media scout",
  "brief.media.mentions": "Last 24 hours",
  "brief.media.mentionsEmpty": "No mentions in the last 24 hours.",
  "brief.media.openWatchdog": "Watchdog",
  "brief.media.suggestion": "Suggestion",
  "brief.urgency.now": "Now",
  "brief.urgency.this_week": "This week",
  "brief.urgency.upcoming": "Upcoming",
  "brief.sentiment.positive": "Positive",
  "brief.sentiment.neutral": "Neutral",
  "brief.sentiment.negative": "Negative",
  "brief.phrase.core": "Core message",
  "brief.phrase.contrast": "Contrast line",
  "brief.phrase.working": "Working translation",
  "brief.phrase.practice": "Practice an answer",
  "brief.loadError": "This section could not load. Try again in a moment.",
  "brief.commandCenter": "Open the full Command Center",
  "brief.editor.title": "Update the scoreboard",
  "brief.editor.hint": "Only figures from the treasurer's reports and the field program's count.",
  "brief.editor.raised": "Cumulative raised (USD)",
  "brief.editor.cash": "Cash on hand (USD)",
  "brief.editor.supporters": "Identified supporters",
  "brief.editor.asOf": "Figures as of",
  "brief.editor.current": "Now: {value}",
  "brief.editor.save": "Save figures",
  "brief.editor.saving": "Saving…",
  "brief.editor.saved": "Scoreboard updated.",
  "brief.editor.invalidMoney": "Enter a dollar amount, for example 12,500.",
  "brief.editor.invalidCount": "Enter a whole number.",
  "brief.editor.nothing": "Enter at least one figure.",
  "brief.editor.failed": "Could not save: {message}",
} as const;

export const briefingEs = {
  "brief.title": "Mi campaña hoy",
  "brief.greet.morning": "Buenos días, {name}",
  "brief.greet.afternoon": "Buenas tardes, {name}",
  "brief.greet.evening": "Buenas noches, {name}",
  "brief.daysToElection": "días para la elección",
  "brief.oneDayToElection": "día para la elección",
  "brief.electionDate": "Martes 3 de noviembre de 2026",
  "brief.electionToday": "Hoy es la elección. A ganarla.",
  "brief.electionOver": "La elección general ya pasó.",
  "brief.daysToRunoff": "días para la segunda vuelta",
  "brief.oneDayToRunoff": "día para la segunda vuelta",
  "brief.runoffDate": "Sábado 12 de diciembre de 2026",
  "brief.runoffToday": "Hoy es la segunda vuelta. A ganarla.",
  "brief.milestone.today": "{title} es hoy",
  "brief.milestone.tomorrow": "{title} mañana",
  "brief.milestone.later": "{title} en {n} días",
  "brief.sec.next": "Lo que sigue",
  "brief.next.callTime": "Empezar las llamadas",
  "brief.next.practice": "Practicar una respuesta",
  "brief.next.tasks": "{n} tarea(s) para hoy",
  "brief.next.tasksNone": "Nada vence hoy",
  "brief.next.scoreboard": "Registrar simpatizantes",
  "brief.next.hoursToday": "{n} h esta semana",
  "brief.sec.scoreboard": "El marcador",
  "brief.sec.tasks": "Lo que te toca hoy",
  "brief.sec.events": "Próximos eventos",
  "brief.sec.media": "Medios",
  "brief.sec.phrase": "Frase del día",
  "brief.supporters.label": "Simpatizantes identificados",
  "brief.supporters.of": "de {target}",
  "brief.supporters.pace": "Faltan {n} · unos {w} por semana",
  "brief.supporters.done": "Meta alcanzada. A seguir sumando.",
  "brief.supporters.empty": "Todavía sin cifra",
  "brief.supporters.emptyHint": "El equipo de campo la registra en el marcador.",
  "brief.raised.label": "Recaudado",
  "brief.raised.of": "de {target} para el {date}",
  "brief.raised.pace": "Hacen falta {amount} al día",
  "brief.raised.reached": "Meta del {date} cumplida.",
  "brief.raised.allDone": "Ya pasaron todas las fechas de informe. Meta final: {target}.",
  "brief.raised.empty": "Todavía sin cifra de recaudación",
  "brief.raised.emptyHint": "La tesorera la registra en el marcador.",
  "brief.raised.cash": "Efectivo en caja {amount}",
  "brief.asOf": "Al {date}",
  "brief.asOfStale": "Al {date} · desactualizado",
  "brief.asOfMissing": "Sin fecha",
  "brief.calls.label": "Llamadas esta semana",
  "brief.calls.ofHours": "de {target} h",
  "brief.calls.rate": "{amount}/hora comprometido · meta {target}",
  "brief.calls.none": "Aún no hay horas registradas esta semana.",
  "brief.calls.open": "Abrir llamadas",
  "brief.alert.conduct.title": "Alerta de conducta",
  "brief.alert.conduct.body":
    "Hay {n} mención(es) sobre ti sin revisar. Habla con la gerencia de campaña antes de dar declaraciones.",
  "brief.alert.conduct.open": "Revisar en Vigilancia",
  "brief.alert.moneyTrigger.title": "Disparador de decisión de dinero",
  "brief.alert.moneyTrigger.body":
    "Faltan {amount} para el umbral acordado. Si en la fecha del disparador seguimos por debajo, se activa el plan acordado:",
  "brief.alert.moneyTrigger.plan": "el presupuesto alterno que la campaña acordó de antemano.",
  "brief.alert.staleRaised.title": "La cifra de dinero está desactualizada",
  "brief.alert.staleRaised.body":
    "Las cifras son al {date}. Pide a la tesorera que actualice el marcador.",
  "brief.alert.staleSupporters.title": "La cifra de simpatizantes está desactualizada",
  "brief.alert.staleSupporters.body":
    "La cifra es al {date}. Pide al equipo de campo que actualice el marcador.",
  "brief.tasks.empty": "Nada pendiente. Buen día para tocar puertas.",
  "brief.tasks.all": "Ver todas",
  "brief.due.overdue": "{n} días de retraso",
  "brief.due.yesterday": "Venció ayer",
  "brief.due.today": "Hoy",
  "brief.due.tomorrow": "Mañana",
  "brief.due.later": "En {n} días",
  "brief.due.none": "Sin fecha",
  "brief.priority.urgent": "Urgente",
  "brief.priority.high": "Alta",
  "brief.status.blocked": "Bloqueada",
  "brief.events.empty": "Todavía no hay eventos en la agenda.",
  "brief.events.all": "Calendario",
  "brief.media.opportunities": "Oportunidades",
  "brief.media.opportunitiesEmpty": "No hay oportunidades nuevas.",
  "brief.media.openScout": "Explorador de medios",
  "brief.media.mentions": "Últimas 24 horas",
  "brief.media.mentionsEmpty": "Sin menciones en las últimas 24 horas.",
  "brief.media.openWatchdog": "Vigilancia",
  "brief.media.suggestion": "Sugerencia",
  "brief.urgency.now": "Ya",
  "brief.urgency.this_week": "Esta semana",
  "brief.urgency.upcoming": "Próxima",
  "brief.sentiment.positive": "Positiva",
  "brief.sentiment.neutral": "Neutral",
  "brief.sentiment.negative": "Negativa",
  "brief.phrase.core": "Mensaje central",
  "brief.phrase.contrast": "Línea de contraste",
  "brief.phrase.working": "Traducción de trabajo",
  "brief.phrase.practice": "Practicar una respuesta",
  "brief.loadError": "No se pudo cargar esta sección. Intenta de nuevo en un momento.",
  "brief.commandCenter": "Abrir el Centro de mando completo",
  "brief.editor.title": "Actualizar el marcador",
  "brief.editor.hint":
    "Solo cifras de los informes de la tesorería y del conteo del programa de campo.",
  "brief.editor.raised": "Recaudado acumulado (USD)",
  "brief.editor.cash": "Efectivo en caja (USD)",
  "brief.editor.supporters": "Simpatizantes identificados",
  "brief.editor.asOf": "Cifras al",
  "brief.editor.current": "Actual: {value}",
  "brief.editor.save": "Guardar cifras",
  "brief.editor.saving": "Guardando…",
  "brief.editor.saved": "Marcador actualizado.",
  "brief.editor.invalidMoney": "Escribe una cantidad en dólares, por ejemplo 12,500.",
  "brief.editor.invalidCount": "Escribe un número entero.",
  "brief.editor.nothing": "Escribe al menos una cifra.",
  "brief.editor.failed": "No se pudo guardar: {message}",
} satisfies Record<keyof typeof briefingEn, string>;
